import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Checkbox,
  Flex,
  Input,
  Spin,
  Table,
  Typography,
  message,
} from 'antd'
import type { TableProps } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import {
  getTransferHours,
  saveTransferHours,
} from '@/api/ai-chat/transferHours'
import { DAY_NAMES } from './utils'

const { Title } = Typography

interface HourRow {
  dow: number
  day: string
  start: string
  end: string
  enabled: boolean
}

const DEFAULT_HOURS: HourRow[] = DAY_NAMES.map((day, dow) => ({
  dow,
  day,
  start: '08:00',
  end: '22:00',
  enabled: true,
}))

export const TransferHoursPage: React.FC = () => {
  const [hours, setHours] = useState<HourRow[]>(DEFAULT_HOURS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getTransferHours()
      setHours(
        DAY_NAMES.map((day, dow) => {
          const row = data[dow] ?? {
            day_of_week: dow,
            start_time: '08:00',
            end_time: '22:00',
            enabled: true,
          }
          return {
            dow,
            day,
            start: row.start_time,
            end: row.end_time,
            enabled: row.enabled,
          }
        }),
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const updateRow = (
    dow: number,
    field: 'start' | 'end' | 'enabled',
    value: string | boolean,
  ) => {
    setHours((prev) =>
      prev.map((r) => (r.dow === dow ? { ...r, [field]: value } : r)),
    )
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await saveTransferHours(
        hours.map((r) => ({
          day: r.dow,
          start: r.start,
          end: r.end,
          enabled: r.enabled,
        })),
      )
      message.success('Saved!')
    } catch {
      message.error('Save failed.')
    } finally {
      setSaving(false)
    }
  }

  const columns: TableProps<HourRow>['columns'] = [
    {
      title: 'Day',
      dataIndex: 'day',
      render: (v: string) => <strong>{v}</strong>,
    },
    {
      title: 'Start',
      dataIndex: 'start',
      render: (v: string, record) => (
        <Input
          type="time"
          value={v}
          onChange={(e) => updateRow(record.dow, 'start', e.target.value)}
          style={{ width: 130, fontSize: 13 }}
        />
      ),
    },
    {
      title: 'End',
      dataIndex: 'end',
      render: (v: string, record) => (
        <Input
          type="time"
          value={v}
          onChange={(e) => updateRow(record.dow, 'end', e.target.value)}
          style={{ width: 130, fontSize: 13 }}
        />
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      render: (v: boolean, record) => (
        <Checkbox
          checked={v}
          onChange={(e) => updateRow(record.dow, 'enabled', e.target.checked)}
        />
      ),
    },
  ]

  return (
    <Spin spinning={loading}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Transfer Hours
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load}>
          Refresh
        </Button>
      </Flex>

      <Card title="Transfer Hours (Eastern Time Zone)">
        <Table<HourRow>
          dataSource={hours}
          columns={columns}
          rowKey="dow"
          pagination={false}
          size="small"
        />
        <Flex align="center" gap={10} style={{ marginTop: 16 }}>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save Hours
          </Button>
        </Flex>
      </Card>
    </Spin>
  )
}
